import * as React from 'react';
import type { OngStats } from '../../types/ong-landing';

interface SocialProofProps {
  stats: OngStats;
}

function formatNumber(n: number): string {
  if (n >= 1000) {
    return n.toLocaleString('es-AR');
  }
  return String(n);
}

export function SocialProof({ stats }: SocialProofProps) {
  if (stats.totalOrders === 0 && stats.topProducts.length === 0) {
    return null;
  }

  return (
    <section className="ong-social-proof">
      <style>{`
        .ong-social-proof {
          padding: 64px 24px;
          background: #fafaf7;
        }
        .ong-social-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .ong-social-proof h2 {
          text-align: center;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 12px;
        }
        .ong-social-proof .section-sub {
          text-align: center;
          font-size: 16px;
          color: #666;
          margin: 0 0 40px;
        }
        .ong-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 40px;
        }
        .ong-stat-card {
          text-align: center;
          padding: 24px 16px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
        }
        .ong-stat-number {
          font-size: 32px;
          font-weight: 800;
          color: #1EAA25;
          line-height: 1;
          margin-bottom: 6px;
        }
        .ong-stat-label {
          font-size: 14px;
          color: #666;
        }
        .ong-top-products {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .ong-top-product {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: #fff;
          border-radius: 10px;
          border: 1px solid #eee;
        }
        .ong-top-product img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .ong-top-product-info {
          min-width: 0;
        }
        .ong-top-product-name {
          font-size: 14px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ong-top-product-stat {
          font-size: 13px;
          color: #1EAA25;
          font-weight: 500;
        }
        @media (max-width: 768px) {
          .ong-stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
          .ong-stat-number {
            font-size: 24px;
          }
          .ong-top-products {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="ong-social-inner">
        <h2>En los últimos 30 días</h2>
        <p className="section-sub">Abastecemos organizaciones sociales todos los días en Córdoba.</p>

        <div className="ong-stats-grid">
          <div className="ong-stat-card">
            <div className="ong-stat-number">{formatNumber(stats.totalKilos)}</div>
            <div className="ong-stat-label">kilos entregados a organizaciones</div>
          </div>
          <div className="ong-stat-card">
            <div className="ong-stat-number">{formatNumber(stats.totalOrders)}</div>
            <div className="ong-stat-label">pedidos de organizaciones sociales</div>
          </div>
          <div className="ong-stat-card">
            <div className="ong-stat-number">{formatNumber(stats.totalCompanies)}</div>
            <div className="ong-stat-label">organizaciones abastecidas</div>
          </div>
        </div>

        {stats.topProducts.length > 0 && (
          <div className="ong-top-products">
            {stats.topProducts.map((p) => (
              <div key={p.product_id} className="ong-top-product">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} loading="lazy" />
                )}
                <div className="ong-top-product-info">
                  <div className="ong-top-product-name">{p.product_name}</div>
                  <div className="ong-top-product-stat">
                    {formatNumber(p.total_units)} {p.unit_short_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
